import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Image as ImageIcon } from '@mui/icons-material';

import { CONCIERGE_SECTIONS, ConciergeSection } from '../types';
import { useConciergeData } from '../hooks/useConcierge';
import { useAppSelector } from '../../../store/hooks';
import { selectSelectedMuseum } from '../../../store/slices/museumSlice';

// Sections that contain image/media filename fields in their edit form
const SECTIONS_WITH_MEDIA = new Set<ConciergeSection>([
  'menu',
  'museum_highlights',
  'eat_drink_shop',
  'donate',
  'contribute',
  'feedback',
]);

const ConciergeListPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedMuseum = useAppSelector(selectSelectedMuseum);
  const { data: conciergeData, loading, error } = useConciergeData(selectedMuseum?.museum_code);

  if (!selectedMuseum) {
    return (
      <Box>
        <Alert severity="info">Select a museum from the sidebar to manage its concierge configuration.</Alert>
      </Box>
    );
  }

  const availableSections = CONCIERGE_SECTIONS.filter(s => !conciergeData || s.key in conciergeData);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Concierge Configuration</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {selectedMuseum.name} — Customise each screen displayed by the concierge robot
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && (
        <Grid container spacing={2}>
          {availableSections.map(section => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={section.key}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {section.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flexShrink: 0 }}>
                      {SECTIONS_WITH_MEDIA.has(section.key) && (
                        <Tooltip title="Requires image upload">
                          <ImageIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        </Tooltip>
                      )}
                      {(section.key === 'admission_prices' || section.key === 'eat_drink_shop') && (
                        <Chip label="ACM" size="small" variant="outlined" />
                      )}
                      {section.key === 'donate' && <Chip label="CCM" size="small" variant="outlined" />}
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {section.description}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() =>
                      navigate(`/content/concierge/${section.key}/edit?museum=${selectedMuseum.museum_code}`)
                    }
                  >
                    Edit
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ConciergeListPage;
